/**
 * Відкриті сторінки — «вкладки» як у 1С (рішення власника 2026-08-24).
 *
 * У 1С внизу екрана — панель відкритих вікон; у нас це смуга під головним
 * меню: перелік НЕЩОДАВНО ВІДКРИТИХ сторінок, між якими клік перемикає.
 * Технічно це не «вікна» (SPA рендерить одну сторінку за раз) — це швидка
 * навігація по вже відвіданих шляхах, як вкладки браузера.
 *
 * Персист: localStorage З КЛЮЧЕМ ПО TENANT (як store/defaults.ts —
 * вкладки не «протікають» між тенантами одного браузера).
 */
import { create } from 'zustand';
import { useAuth } from '@/auth/store';
import { isPageLeaving } from './pageLeaving';
import { isTabbableRoute } from './tabTitle';

export interface OpenTab {
  /** шлях сторінки (pathname) — ключ вкладки */
  path: string;
  /** ч.1378 (SUP-1329FE54): повна адреса, на якій вкладку залишили (pathname + search) —
   *  повернення відкриває звіт з тими самими періодом, рахунком і відборами. */
  href?: string;
  /** людська назва (з меню або уточнена сторінкою через useTabTitle) */
  title: string;
  /** true — назву задала сама сторінка (не перезаписувати з меню) */
  pinnedTitle?: boolean;
}

/** Скільки вкладок тримаємо (найстаріші витісняються). */
export const MAX_TABS = 12;

function keyForTenant(tenantId: string): string {
  return `openTabs.${tenantId}`;
}

function storageKey(): string {
  return keyForTenant(useAuth.getState().activeTenantId ?? 'no-tenant');
}

function load(): OpenTab[] {
  try {
    const raw = localStorage.getItem(storageKey());
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    // Вкладки-списки, збережені до зміни правила (2026-08-24), відсіюємо.
    return parsed
      .filter((x: OpenTab) => x?.path && isTabbableRoute(x.path))
      .slice(-MAX_TABS);
  } catch {
    return [];
  }
}

function persist(tabs: OpenTab[]): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(tabs));
  } catch {
    /* приватний режим / переповнене сховище — вкладки просто не переживуть F5 */
  }
}

interface OpenTabsState {
  tabs: OpenTab[];
  /** Відкрити/підняти вкладку (виклик на кожній зміні маршруту). */
  open: (path: string, title: string) => void;
  /** Уточнити назву поточної вкладки (useTabTitle у редакторах). */
  rename: (path: string, title: string) => void;
  /** Назва з заголовка сторінки (автоматично) — НЕ перебиває явну. */
  autoRename: (path: string, title: string) => void;
  /** ч.1378: запамʼятати повну адресу відкритої вкладки (параметри звіту, відбори списку). */
  remember: (path: string, href: string) => void;
  close: (path: string) => void;
  /**
   * ч.950 (баг власника): документ ЗБЕРЕЖЕНО — вкладка «…/new» стає вкладкою
   * збереженого документа (`…/{id}`), а не дублюється другою. Якщо вкладка на
   * `…/{id}` уже є — стару «/new» просто прибираємо.
   */
  replacePath: (fromPath: string, toPath: string, title?: string) => void;
  /**
   * ч.950 (баг власника): документ ВИДАЛЕНО — його вкладка закривається
   * (інакше лишалась «у кеші» і збивала з пантелику).
   */
  closeByDocId: (docId: string) => void;
  closeOthers: (path: string) => void;
  closeAll: () => void;
}

export const useOpenTabs = create<OpenTabsState>((set, get) => {
  /** Записати новий перелік. ч.1197: після скидання при зміні клієнта сторінка
   *  доживає останні мілісекунди — нічого більше не пишемо (isPageLeaving). */
  const commit = (next: OpenTab[]): void => {
    if (isPageLeaving()) return;
    persist(next);
    set({ tabs: next });
  };
  return {
  tabs: load(),

  open: (path, title) => {
    const tabs = get().tabs;
    const existing = tabs.find((x) => x.path === path);
    if (existing) {
      // Вже відкрита: не чіпаємо порядок (як у 1С — вкладка стоїть на місці),
      // лише оновлюємо назву, якщо сторінка її ще не уточнила.
      if (!existing.pinnedTitle && existing.title !== title) {
        const next = tabs.map((x) => x.path === path ? { ...x, title } : x);
        commit(next);
      }
      return;
    }
    const next = [...tabs, { path, title }].slice(-MAX_TABS);
    commit(next);
  },

  rename: (path, title) => {
    const tabs = get().tabs;
    const cur = tabs.find((x) => x.path === path);
    if (!cur || (cur.pinnedTitle && cur.title === title)) return;
    const next = tabs.map((x) =>
      x.path === path ? { ...x, title, pinnedTitle: true } : x);
    commit(next);
  },

  autoRename: (path, title) => {
    const tabs = get().tabs;
    const cur = tabs.find((x) => x.path === path);
    if (!cur || cur.pinnedTitle || cur.title === title) return;
    const next = tabs.map((x) => x.path === path ? { ...x, title } : x);
    commit(next);
  },

  remember: (path, href) => {
    const tabs = get().tabs;
    const cur = tabs.find((x) => x.path === path);
    if (!cur || (cur.href ?? cur.path) === href) return;
    commit(tabs.map((x) => x.path === path ? { ...x, href: href === path ? undefined : href } : x));
  },

  close: (path) => {
    const next = get().tabs.filter((x) => x.path !== path);
    commit(next);
  },

  replacePath: (fromPath, toPath, title) => {
    const tabs = get().tabs;
    const src = tabs.find((x) => x.path === fromPath);
    if (!src && !tabs.some((x) => x.path === toPath)) return;
    const next: OpenTab[] = [];
    for (const x of tabs) {
      if (x.path === toPath) continue;            // існуючу ціль перезапишемо нижче
      if (x.path === fromPath) {
        next.push({ path: toPath, title: title ?? x.title, pinnedTitle: !!title || x.pinnedTitle });
        continue;
      }
      next.push(x);
    }
    // Якщо «/new» вкладки не було (F5 на збереженому) — просто додаємо ціль.
    if (!next.some((x) => x.path === toPath)) {
      next.push({ path: toPath, title: title ?? toPath, pinnedTitle: !!title });
    }
    const trimmed = next.slice(-MAX_TABS);
    commit(trimmed);
  },

  closeByDocId: (docId) => {
    if (!docId) return;
    const next = get().tabs.filter(
      (x) => !x.path.split('?')[0]!.endsWith(`/${docId}`));
    if (next.length === get().tabs.length) return;
    commit(next);
  },

  closeOthers: (path) => {
    const next = get().tabs.filter((x) => x.path === path);
    commit(next);
  },

  closeAll: () => {
    commit([]);
  },
  };
});

/**
 * ч.1197 (рішення власника): зміна клієнта закриває ВСІ вкладки — і клієнта,
 * якого покидаємо, і збережений раніше перелік клієнта, до якого переходимо
 * (щоб після перезавантаження смуга вкладок починалась з чистого аркуша).
 * Викликати ДО markPageLeaving().
 */
export function resetTabsForTenantSwitch(targetTenantId: string): void {
  useOpenTabs.getState().closeAll();
  try {
    localStorage.removeItem(keyForTenant(targetTenantId));
  } catch { /* ignore */ }
}
